import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCreateFontFamily, uploadFontFile } from "@/hooks/use-font-families";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Props {
  brandId: string;
  existingFamilyId?: string;
  initialName?: string;
  onSaved: (familyId: string) => void;
  onCancel: () => void;
}

interface PendingFile { file: File; weight: number; style: "normal" | "italic" }

const WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

function guessWeight(name: string): number {
  const lower = name.toLowerCase();
  if (lower.includes("thin")) return 100;
  if (lower.includes("extralight") || lower.includes("ultralight")) return 200;
  if (lower.includes("light")) return 300;
  if (lower.includes("medium")) return 500;
  if (lower.includes("semibold") || lower.includes("demibold")) return 600;
  if (lower.includes("extrabold") || lower.includes("ultrabold")) return 800;
  if (lower.includes("black") || lower.includes("heavy")) return 900;
  if (lower.includes("bold")) return 700;
  return 400;
}

export function CustomFontFamilyEditor({ brandId, existingFamilyId, initialName, onSaved, onCancel }: Props) {
  const { t } = useTranslation("settings");
  const [name, setName] = useState(initialName ?? "");
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [busy, setBusy] = useState(false);
  const createFamily = useCreateFontFamily(brandId);
  const qc = useQueryClient();

  function addFiles(files: FileList | null) {
    if (!files) return;
    const next: PendingFile[] = Array.from(files).map((f) => ({
      file: f,
      weight: guessWeight(f.name),
      style: f.name.toLowerCase().includes("italic") ? "italic" : "normal",
    }));
    setPending((p) => [...p, ...next]);
  }

  async function save() {
    if (!name.trim()) { toast.error(t("fontFamily.familyNameRequired")); return; }
    if (pending.length === 0 && !existingFamilyId) { toast.error(t("fontFamily.addFontFile")); return; }
    setBusy(true);
    try {
      let familyId = existingFamilyId;
      if (!familyId) {
        const fam = await createFamily.mutateAsync({ name: name.trim(), source: "CUSTOM" });
        familyId = fam.id;
      }
      for (const pf of pending) {
        await uploadFontFile({ brandId, fontFamilyId: familyId!, file: pf.file, fontWeight: pf.weight, fontStyle: pf.style });
      }
      await qc.invalidateQueries({ queryKey: ["font-families", brandId] });
      toast.success(t("fontFamily.fontFamilySaved"));
      onSaved(familyId!);
    } catch (e: any) {
      toast.error(e?.message ?? t("fontFamily.saveFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-sm font-medium">{t("fontFamily.familyName")}</label>
        <input
          className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme Sans"
          disabled={!!existingFamilyId}
        />
      </div>
      <div>
        <label className="text-sm font-medium">{t("fontFamily.fontFiles")}</label>
        <input
          type="file"
          multiple
          accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
          onChange={(e) => addFiles(e.target.files)}
          className="mt-1 block text-sm"
        />
        <ul className="mt-2 space-y-2">
          {pending.map((pf, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm">
              <span className="flex-1 truncate">{pf.file.name}</span>
              <select
                value={pf.weight}
                onChange={(e) => setPending((p) => p.map((x, i) => (i === idx ? { ...x, weight: Number(e.target.value) } : x)))}
                className="rounded-md border bg-background px-1 py-0.5"
              >
                {WEIGHTS.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
              <select
                value={pf.style}
                onChange={(e) => setPending((p) => p.map((x, i) => (i === idx ? { ...x, style: e.target.value as "normal" | "italic" } : x)))}
                className="rounded-md border bg-background px-1 py-0.5"
              >
                <option value="normal">{t("fontFamily.weightNormal")}</option>
                <option value="italic">{t("fontFamily.weightItalic")}</option>
              </select>
              <Button variant="ghost" size="sm" onClick={() => setPending((p) => p.filter((_, i) => i !== idx))}>×</Button>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={busy}>{t("actions.cancel", { ns: "common" })}</Button>
        <Button onClick={save} disabled={busy}>{busy ? t("fontFamily.savingFamily") : t("fontFamily.saveFamily")}</Button>
      </div>
    </div>
  );
}
